import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, TrendingUp, Award, Search, Mail, Download, MessageCircle, Calendar, Clock, ArrowRight, Zap, FileBarChart, Send, HelpCircle, CheckCircle, AlertCircle, Activity, Filter, ExternalLink, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { AIInsightPopover } from '../components/AIInsightPopover';
export function AdminHub() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [reminderLearner, setReminderLearner] = useState('');
  const [isReminderDropdownOpen, setIsReminderDropdownOpen] = useState(false);
  const [timePeriod, setTimePeriod] = useState<'7d' | '30d'>('30d');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const reminderDropdownRef = useRef<HTMLDivElement>(null);
  const incompleteLearners = [{
    value: 'rachel-patel',
    name: 'Rachel Patel',
    email: 'rachel.patel@example.com'
  }, {
    value: 'david-nguyen',
    name: 'David Nguyen',
    email: 'd.nguyen@example.com'
  }, {
    value: 'karen-brooks',
    name: 'Karen Brooks',
    email: 'k.brooks@example.com'
  }, {
    value: 'marcus-hall',
    name: 'Marcus Hall',
    email: 'marcus.h@example.com'
  }, {
    value: 'tanya-foster',
    name: 'Tanya Foster',
    email: 'tanya.f@example.com'
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (reminderDropdownRef.current && !reminderDropdownRef.current.contains(event.target as Node)) {
        setIsReminderDropdownOpen(false);
      }
    };
    if (isUserMenuOpen || isReminderDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isReminderDropdownOpen]);
  const navigationCards = [{
    title: 'Analytics Dashboard',
    description: 'View detailed performance metrics and insights',
    icon: BarChart3,
    color: 'from-[#00638C] to-[#30718D]',
    stats: '248 learners tracked',
    link: '/analytics'
  }, {
    title: 'User Management',
    description: 'Manage learner accounts and permissions',
    icon: UserCog,
    color: 'from-[#30718D] to-[#265a70]',
    stats: '18 pending invites',
    link: '/assessment'
  }, {
    title: 'Reports',
    description: 'Generate and download custom reports',
    icon: FileText,
    color: 'from-[#007558] to-[#005a44]',
    stats: 'Last report: 1 hour ago',
    link: '/feedback'
  }, {
    title: 'Insights & Actions',
    description: 'Prioritize learner support with AI-powered skill insights',
    icon: Zap,
    color: 'from-[#DB3725] to-[#DB3725]',
    stats: '4 priority insights',
    link: '/insights-actions'
  }];
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1645" data-mp="2heb8ysixi3s7">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1646" data-mp="1kplmeowcwmzw">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1647" data-mp="1oq5irq93vmkz">
          <div className="flex items-center h-16" data-id="element-1648" data-mp="1os89kci31jqr">
            {/* Logo */}
            <div className="text-[#E53E3E] font-bold text-xl" data-id="element-1649" data-mp="1togsm0zehx37">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-1650" data-mp="1ypwe1u5hq5m4">
              <Link to="/admin" className="flex items-center gap-2 text-[#00638C] border-b-2 border-[#00638C] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-1651" data-mp="353cbjz52ykpk">
                <Home size={18} data-id="element-1652" data-mp="mvknzdqmyanc" />
                <span className="hidden sm:inline" data-id="element-1653" data-mp="hvy1sestxk2e">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1654" data-mp="hyasphgkf4rl">
                <BarChart3 size={18} data-id="element-1655" data-mp="qyqveophi7ve" />
                <span className="hidden sm:inline" data-id="element-1656" data-mp="15tdgdm3mv26h">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1657" data-mp="10v27bdl5wvpb">
                <FileText size={18} data-id="element-1658" data-mp="1aklqkkcserkp" />
                <span className="hidden sm:inline" data-id="element-1659" data-mp="1aloj0wprd22q">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1660" data-mp="1ojlgdvc1dcig">
                <UserCog size={18} data-id="element-1661" data-mp="236k7fp6ze4wn" />
                <span className="hidden sm:inline" data-id="element-1662" data-mp="237mwdrkqtx9x">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-1663" data-mp="1t8xkqgjfx924">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1664" data-mp="1tb0bx3wbx88f">
                <Bell size={20} data-id="element-1665" data-mp="1kfkn51aflj9p" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1666" data-mp="1kfavzadnh62f"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1667" data-mp="1p95dq89p3ayv">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1668" data-mp="1ajqywknco1c3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1669" data-mp="11ijcazu74oxu">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-1670" data-mp="387l434izqg3m">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1671" data-mp="2yfxihk947ta2" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1672" data-mp="2ti5dppndgcog">
                    <div className="py-2" data-id="element-1673" data-mp="3r431o2kita7s">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1674" data-mp="3m59pcdgyzp97">
                        <Users size={18} className="text-gray-600" data-id="element-1675" data-mp="3d41t00y0kyhr" />
                        <span className="font-medium text-gray-900" data-id="element-1676" data-mp="25r9tpep5qlrr">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1677" data-mp="21omyd3ta2ipt">
                        <Home size={18} className="text-gray-600" data-id="element-1678" data-mp="l97kb54hnkx1" />
                        <span className="font-medium text-gray-900" data-id="element-1679" data-mp="l7ebbj6ekolv">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1680" data-mp="v029oid170mp">
                        <UserCog size={18} className="text-gray-600" data-id="element-1681" data-mp="100q298gwrpj1" />
                        <span className="font-medium text-gray-900" data-id="element-1682" data-mp="14vn5fyrib4o0">
                          Admin Hub
                        </span>
                      </button>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1683" data-mp="1dyelkbvbuolz">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1684" data-mp="3fq1eiiys4tl3">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-1685" data-mp="12ijl4u4gwpib"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-1686" data-mp="4osffeb6eyf5"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1687" data-mp="9owmkdq72gsp">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-1688" data-mp="3rw2x9qtsquiy"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1689" data-mp="o8iobuilwtj">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1690" data-mp="sev3zkfvtm90">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1691" data-mp="1c3nc55nim6jw"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1692" data-mp="q23hqnc45jmg"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1693" data-mp="uzwmdbxrq5km"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1694" data-mp="h0nh37b3ovvf">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1695" data-mp="c2c6guczehc5">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-1696" data-mp="3tits1eeqaity">
          <div data-id="element-1697" data-mp="2ciye3c1skcn">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#30718D] mb-1" data-id="element-1698" data-mp="2c93q2g25p0m">
              Administrator
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-1699" data-mp="3pg77zzoau27b">
              Welcome Back, Linda
            </h1>
          </div>
          <button className="text-[#DB3725] hover:text-[#c02f1f] font-medium text-sm transition-colors self-start sm:self-auto flex items-center gap-1.5" data-id="element-1700" data-mp="35tlwbbb74raa">
            <Mail size={14} data-id="element-1701" data-mp="30xnanvh95ndk" />
            Contact support
          </button>
        </div>

        {/* Company & Send Email Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-1702" data-mp="3asypedsnuulb">
          {/* Company Info Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.05
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex flex-col" data-phoebe-tour="company-info" data-id="element-1703" data-mp="3oshmmbjxoliu">
            <div className="flex items-center gap-3 mb-4" data-id="element-1704" data-mp="bfek6r1f9gip">
              <div className="w-8 h-8 rounded-lg bg-[#30718D]/10 flex items-center justify-center flex-shrink-0" data-id="element-1705" data-mp="6fa595g5wtul">
                <Building2 size={16} className="text-[#30718D]" data-id="element-1706" data-mp="307rvqxnoucqf" />
              </div>
              <div data-id="element-1707" data-mp="356cns9ssdq3s">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1708" data-mp="355jn9qkf7wu3">
                  Company Name
                </div>
                <div className="text-base font-semibold text-gray-900" data-id="element-1709" data-mp="395troyskef6u">
                  Lorem Ipsum
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100" data-id="element-1710" data-mp="3iwofi7lglmue">
              <div className="flex items-center gap-3" data-id="element-1711" data-mp="3nwu9huf7blfn">
                <div className="w-8 h-8 rounded-lg bg-[#007558]/10 flex items-center justify-center flex-shrink-0" data-id="element-1712" data-mp="3nv9fc5wsu4ib">
                  <Users size={16} className="text-[#007558]" data-id="element-1713" data-mp="3stt3mcyv7sdu" />
                </div>
                <div data-id="element-1714" data-mp="rl82g80syqt">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1715" data-mp="ps9lo7k9ktw">
                    Registered Learners
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-1716" data-mp="22k9boqokto74">
                    4 Learners
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-3" data-id="element-1717" data-mp="1q87afa5pabne">
              <div className="flex items-center gap-3" data-id="element-1718" data-mp="1q7xj9j8x5yg4">
                <div className="w-8 h-8 rounded-lg bg-[#DB3725]/10 flex items-center justify-center flex-shrink-0" data-id="element-1719" data-mp="1l9dxbiubsmnr">
                  <Clock size={16} className="text-[#DB3725]" data-id="element-1720" data-mp="1h9v9ofb4dxh8" />
                </div>
                <div data-id="element-1721" data-mp="2dz2v7nbqo6l5">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1722" data-mp="2dyb1ki1pcixi">
                    Pending
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-1723" data-mp="24xwdjaxcspqb">
                    5 Learners
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Send Account Creation Email */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2" data-phoebe-tour="send-email" data-id="element-1724" data-mp="tkkereggaxb0">
            <div className="flex items-center gap-3 mb-4" data-id="element-1725" data-mp="23hr0f7w3nhtt">
              <div className="w-10 h-10 rounded-lg bg-[#30718D]/10 flex items-center justify-center" data-id="element-1726" data-mp="27gq5cshtzel9">
                <Mail size={20} className="text-[#30718D]" data-id="element-1727" data-mp="2v859mqt645z5" />
              </div>
              <div data-id="element-1728" data-mp="306oxiwrbnru5">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1729" data-mp="30543d88x6awt">
                  Send Account Creation Email
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1730" data-mp="2m64f41pbjy16">
                  Remind learners to complete account setup.{' '}
                  <span className="font-semibold text-[#DB3725]" data-id="element-1731" data-mp="2r7bilokfib21">
                    5 pending
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-3 items-end" data-id="element-1732" data-mp="1frmz576dvu5z">
              <div className="relative flex-1" ref={reminderDropdownRef} data-id="element-1733" data-mp="178jn1mr06vg4">
                <button onClick={() => {
                setIsReminderDropdownOpen(!isReminderDropdownOpen);
              }} className="flex items-center gap-2 w-full px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-medium" data-id="element-1734" data-mp="137pycr5qlcm6">
                  <Users size={18} className="text-[#30718D]" data-id="element-1735" data-mp="ojv2j1wfms4v" />
                  <span className="flex-1 text-left text-gray-700" data-id="element-1736" data-mp="jk0dl3w7wcmg">
                    {reminderLearner ? incompleteLearners.find(l => l.value === reminderLearner)?.name : 'All incomplete learners (5)'}
                  </span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isReminderDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1737" data-mp="5raga3b8nla1" />
                </button>

                {isReminderDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto" data-id="element-1738" data-mp="25ubnf67f5tld">
                    <button onClick={() => {
                  setReminderLearner('');
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm ${reminderLearner === '' ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-1739" data-mp="2altlnmi25g2n">
                      All incomplete learners
                    </button>
                    {incompleteLearners.map(learner => <button key={learner.value} onClick={() => {
                  setReminderLearner(learner.value);
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${reminderLearner === learner.value ? 'bg-[#30718D]/5' : ''}`} data-id="element-1740" data-mp="32bg2e3gtsmlx">
                        <div className={`font-medium text-sm ${reminderLearner === learner.value ? 'text-[#30718D]' : 'text-gray-900'}`} data-id="element-1741" data-mp="33ow45z6pcwch">
                          {learner.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1742" data-mp="2ts9k3cm7a9bi">
                          {learner.email}
                        </div>
                      </button>)}
                  </div>}
              </div>
              <button className="px-4 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-1743" data-mp="myflexoihkii">
                <Send size={16} data-id="element-1744" data-mp="126vibnde035n" />
                Send
              </button>
            </div>
          </motion.div>
        </div>

        {/* Performance Snapshot */}
        <div data-phoebe-tour="dashboard" className="mb-8" data-id="element-1745" data-mp="167fhsxlfniuy">
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1746" data-mp="sfr3qormikbc">
            Performance Snapshot
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1747" data-mp="xelwaife7n8n">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-1748" data-mp="ihl64jcuo0ca">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1749" data-mp="23xabmug5fbg8">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-1750" data-mp="28usb7u8a0ebk">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1751" data-mp="28szep7rdnaha">
                    55
                  </span>
                  <span className="align-middle text-sm" data-id="element-1752" data-mp="2e0fo3p43hmtg">
                    of your invited learners have
                    <br data-id="element-1753" data-mp="2rrkfy5181at8" />
                    completed registration
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#007558] text-sm font-medium" data-id="element-1754" data-mp="2mzj6srzco07f">
                  <span className="text-xs" data-id="element-1755" data-mp="31offmr98dh93">▲</span>
                  <span data-id="element-1756" data-mp="31rbdkfl2w5xj">16% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-1757" data-mp="2wshwyeahvyt5"></div>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-1758" data-mp="36ola0f6vxhuv">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1759" data-mp="eps8m5xk0s76">
                <div className="flex items-start justify-between mb-3 gap-2" data-id="element-1760" data-mp="ep8pwn02xzs3">
                  <div className="text-gray-700 leading-relaxed" data-id="element-1761" data-mp="td3je5lww868">
                    <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1762" data-mp="ofk3705nllbn">
                      {timePeriod === '7d' ? '65%' : '80%'}
                    </span>
                    <span className="align-middle text-sm" data-id="element-1763" data-mp="rkes6vte708">
                      of your learners have
                      <br data-id="element-1764" data-mp="9qfmjneibp5n" />
                      spent time learning this{' '}
                      {timePeriod === '7d' ? 'week' : 'month'}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 flex-shrink-0" data-id="element-1765" data-mp="5r6sehfk1p9k">
                    <AIInsightPopover headline="Learning participation is gaining momentum" insightBeforeMetric="Monthly participation reached" highlightedMetric="80%" insightAfterMetric="." explanation="Sustaining this pace with a timely reminder can help convert active learners into completed modules." insightId={6} data-id="element-1766" data-mp="1l3rizd7xt5hs" />
                    <div className="flex items-center bg-gray-100 rounded-md p-px" data-id="element-1767" data-mp="1jpgnzonaars0">
                      <button onClick={() => setTimePeriod('7d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '7d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1768" data-mp="27dq6aglagxee">
                        7 Days
                      </button>
                      <button onClick={() => setTimePeriod('30d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '30d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1769" data-mp="2qcm2tykmdnve">
                        30 Days
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-sm font-medium ${timePeriod === '7d' ? 'text-[#007558]' : 'text-[#007558]'}`} data-id="element-1770" data-mp="2r01qfcxp7t3m">
                  <span className="text-xs" data-id="element-1771" data-mp="2m2jfjh04f3sz">▲</span>
                  <span data-id="element-1772" data-mp="2h96v7j2h5j61">
                    {timePeriod === '7d' ? '12% vs prior 7 days' : '20% vs 30 days ago'}
                  </span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-1773" data-mp="28b4y3elv3ao1"></div>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-1774" data-mp="23c1t1u5vt4g2">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1775" data-mp="rsutr7rdnxtx">
                <div className="mb-3 flex items-start justify-between gap-3 text-gray-700 leading-relaxed" data-id="element-1776" data-mp="rrjnn1agmdzn">
                  <div data-id="element-1777" data-mp="3r9ja6bui17wp">
                    <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1778" data-mp="3r65ajbd7zcsy">
                      10%
                    </span>
                    <span className="align-middle text-sm" data-id="element-1779" data-mp="430kt1a6itey">
                      of your learners have
                      <br data-id="element-1780" data-mp="8v0moz9t7we6" />
                      completed all skill modules
                    </span>
                  </div>
                  <AIInsightPopover headline="Completion needs a focused follow-up" insightBeforeMetric="Only" highlightedMetric="10%" insightAfterMetric=" of learners have completed every module." explanation="Completion fell 20% from the prior period. Prioritize active learners with one or two modules remaining." insightId={7} data-id="element-1781" data-mp="38h8ep326cc8m" />
                </div>
                <div className="flex items-center gap-1.5 text-[#DB3725] text-sm font-medium" data-id="element-1782" data-mp="2ybz3s5z3lu2i">
                  <span className="text-xs" data-id="element-1783" data-mp="3c3djo3xplf5c">▼</span>
                  <span data-id="element-1784" data-mp="3763wyw1pfhl7">20% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#DB3725]" data-id="element-1785" data-mp="3m34e65m9ru5f"></div>
            </motion.div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div data-phoebe-tour="navigation-cards" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-1786" data-mp="3h5cozjd0em4x">
          {navigationCards.map((card, index) => {
          const Icon = card.icon;
          return <Link key={index} to={card.link} data-id="element-1787" data-mp="1ms3999tve4e8" data-mpi={index}>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + index * 0.1
            }} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-1788" data-mp="27pwyobxe53l">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-1789" data-mp="3jpisjpxpoaky">
                    <Icon size={24} className="text-white" data-id="element-1790" data-mp="3aoko56jd211u" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#00638C] transition-colors" data-id="element-1791" data-mp="13x1vvdi5osti">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-1792" data-mp="17ezh75nfsech">
                    {card.description}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-1793" data-mp="12fwjk69sdaej">
                    <span className="text-xs font-medium text-gray-500" data-id="element-1794" data-mp="xh33q77117b7">
                      {card.stats}
                    </span>
                    <ArrowRight size={16} className="text-gray-400 group-hover:text-[#00638C] group-hover:translate-x-1 transition-all" data-id="element-1795" data-mp="sj1mzsx2pk2w" />
                  </div>
                </motion.div>
              </Link>;
        })}
        </div>

        {/* Contact Support - Full Width Horizontal */}
        <motion.div data-phoebe-tour="support" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="bg-gradient-to-br from-[#30718D] to-[#265a70] rounded-xl p-6 shadow-lg text-white mb-8" data-id="element-1796" data-mp="1bh46js6uqotb">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-id="element-1797" data-mp="2gfrdzfav4i0n">
            <div className="flex items-center gap-4" data-id="element-1798" data-mp="2lf3y3sacnazj">
              <div className="w-12 h-12 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-1799" data-mp="2ld9dyg6wjmwi">
                <HelpCircle size={24} className="text-white" data-id="element-1800" data-mp="2qbko6s13zzf9" />
              </div>
              <div data-id="element-1801" data-mp="2vaxjm161qetg">
                <h3 className="text-xl font-bold mb-1" data-id="element-1802" data-mp="2zelsqoabtigt">Need Help?</h3>
                <p className="text-sm text-white/70 max-w-2xl" data-id="element-1803" data-mp="2zcskj4k2eq6p">
                  Our support team is available 24/7 to help you with any
                  questions or issues you may have.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end justify-center gap-3 w-full md:w-auto" data-id="element-1804" data-mp="3e41gdvek97sa">
              <button className="w-full md:w-auto px-6 py-3 bg-white text-[#30718D] rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm font-semibold shadow-sm" data-id="element-1805" data-mp="3j1t74m3gynuw">
                <Mail size={16} data-id="element-1806" data-mp="39vc3emrzy4w" />
                Email Support
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}